/**
 * Days and times as the portal shows them, in the run browser and the metrics alike: in the
 * browser's local time, never UTC (docs/roadmap.md#admin-portal).
 */

/** The browser's time zone, as IANA names it (`America/Chicago`). */
export function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const two = (value: number) => String(value).padStart(2, "0");

const dayOf = (at: Date) => `${at.getFullYear()}-${two(at.getMonth() + 1)}-${two(at.getDate())}`;

/** The local day of an instant, `YYYY-MM-DD`: today by default. */
export function localDay(at: Date = new Date()): string {
  return dayOf(at);
}

/**
 * A local day, `YYYY-MM-DD`, as the window `runsOfDay` takes: from its local midnight (included)
 * to the next local midnight (left out), as ISO instants. A day with a daylight-saving change is
 * 23 or 25 hours long; where midnight itself is skipped, the day starts at the first time it has.
 */
export function localDayWindow(date: string): { from: string; to: string } {
  const [year, month, day] = date.split("-").map(Number) as [number, number, number];
  return {
    from: new Date(year, month - 1, day).toISOString(),
    to: new Date(year, month - 1, day + 1).toISOString(),
  };
}

/** An ISO time in local time, `YYYY-MM-DD HH:MM:SS`; the text unchanged when it is not a time. */
export function localTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return `${dayOf(at)} ${two(at.getHours())}:${two(at.getMinutes())}:${two(at.getSeconds())}`;
}
